import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import notify from 'devextreme/ui/notify';

import { AuthService } from '../auth/auth.service';

/** Endpoints de autenticação que nunca disparam refresh silencioso (evita loop). */
const AUTH_ENDPOINT_PATHS = [
  '/api/login/refresh-token',
  '/api/login/forgot-password',
  '/api/login/reset-password',
  '/api/register',
];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINT_PATHS.some(path => url.includes(path)) || /\/api\/login\/?$/.test(url);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // 401 fora dos endpoints de auth → refresh silencioso + repete 1x (ARC-042).
      // Só desloga se o refresh falhar ou se a repetição voltar 401.
      if (error.status === 401 && !isAuthEndpoint(req.url)) {
        return authService.refreshSession().pipe(
          catchError(() => {
            // Refresh indisponível ou recusado: sessão expirada — limpa tudo (ARC-008).
            authService.logout();
            return throwError(() => error);
          }),
          switchMap(() => {
            const retried = req.clone({
              setHeaders: { Authorization: `Bearer ${authService.getAccessToken() ?? ''}` },
            });
            return next(retried).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (retryError.status === 401) {
                  authService.logout();
                }
                return throwError(() => retryError);
              })
            );
          })
        );
      }

      if (error.status === 403) {
        // ARC-041: toast global de acesso negado. Mantém o NÃO-deslogar + rethrow
        // atuais — 403 é falta de permissão no tenant/role, não sessão expirada.
        console.warn('Acesso negado:', error.url);
        notify('Acesso negado: você não tem permissão para esta ação.', 'error', 3500);
        return throwError(() => error);
      }

      if (error.status >= 500) {
        console.error('Erro no servidor:', error.status, error.url);
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};
